/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import multiguard from 'vue-router-multiguard';
import { Store } from 'vuex';
import { Route } from 'vue-router';

import { keysToCamel } from '@/utils';
import AuditSetupWizard from '@/pages/audit_setup_wizard/AuditSetupWizard.vue';
import AuthenticationSettingsPage from '@/pages/settings/authentication/AuthenticationSettingsPage.vue';
import AuthenticationSettingsSaml from '@/pages/settings/authentication/saml/AuthenticationSettingsSaml.vue';
import EmailSettingsPage from '@/pages/settings/email/EmailSettingsPage.vue';
import servicenowSettings from '@/pages/settings/servicenow/servicenowSettingsPage.vue';
import servicenowStep1Settings from '@/pages/settings/servicenow/step1.vue';
import Error404Page from '@/pages/error/Error404Page.vue';
import ConnectorsPage from '@/pages/settings/connectors/ConnectorsPage.vue';
import EmbeddedDashboardPage from '@/pages/dashboards/embedded/EmbeddedDashboardPage.vue';
import DashboardListPage from '@/pages/dashboards/DashboardListPage.vue';
import DashboardPage from '@/pages/dashboards/DashboardPage.vue';
import ExportPage from '@/pages/export/ExportPage.vue';
import ImportPage from '@/pages/import/ImportPage.vue';
import IntegrationSettingsPage from '@/pages/settings/integrations/IntegrationSettingsPage.vue';
import ObjectMapperPage from '@/pages/object_mapper/ObjectMapperPage.vue';
import PSSOScopingPage from '@/pages/program_scoping/PSSOScopingPage.vue';
import ProgramScopingPage from '@/pages/program_scoping/ProgramScopingPage.vue';
import ProfilePage from '@/pages/profile/UserProfile.vue';
import RiskHeatmapPage from '@/pages/risk_heatmap/RiskHeatmapPage.vue';
import RiskSettingsPage from '@/pages/risk_settings/RiskSettingsPage.vue';
import WorkflowsPage from '@/pages/workflows/WorkflowsPage.vue';
import SAMLSettingsPage from '@/pages/settings/saml/SAMLSettingsPage.vue';
import SurveyBuilderPage from '@/pages/survey_builder/SurveyBuilderPage.vue';
import SurveySendingPage from '@/pages/survey_sending/SurveySendingPage.vue';
import ToDoListPage from '@/pages/todo_list/ToDoListPage.vue';
import SurveyReSendingPage from '@/pages/survey_sending/SurveyReSendingPage.vue';
import WatchUnsubscribePage from '@/pages/notifications/watch/WatchUnsubscribePage.vue';
import SorListingPage from '@/pages/listing/SorListingPage.vue';
import NewObjectPage from '@/pages/sor/new_object/NewObjectPage.vue';
import NewDashboardPage from '@/pages/dashboards/NewDashboardPage.vue';
import Benchmarks from '@/pages/benchmarks/Benchmarks.vue';
import Benchmark from '@/pages/benchmarks/Benchmark.vue';
import RiskAllocation from '@/pages/risk-allocation/RiskAllocation.vue';
// TODO: during development only
import EvidenceCollection from '@/components/cards/evidence_collection/EvidenceCollection.vue';
import Embedded from '@/components/Embedded.vue';

import { allRoles, adminRoles, behindFeatureFlag, canNavigateToRoute, canNavigateToNewSor } from './guards';
import { PATHS } from './paths';

export function createRoutes(store: Store<any>) {
  return [
    // TODO: during development only
    {
      name: 'dev/evidenceCollection',
      path: '/dev/evidence-collection/:type/:id',
      props: true,
      component: EvidenceCollection,
      beforeEnter: behindFeatureFlag(store, 'evidence_collection_page'),
    },
    {
      ...PATHS.TODOLIST.CATEGORY,
      redirect: to => ({
        name: PATHS.TODOLIST.CATEGORY_OBJECT.name,
        params: {
          category: to.params.category || 'todo',
        },
      }),
    },
    {
      ...PATHS.TODOLIST.CATEGORY_OBJECT,
      component: ToDoListPage,
      props: route => ({
        categoryInput: route.params.category,
        objectTypeInput: route.params.objectType,
      }),
      beforeEnter: multiguard([behindFeatureFlag(store, 'todo_list_in_vue'), canNavigateToRoute(store, allRoles)]),
    },
    {
      ...PATHS.SOR.LISTING,
      redirect: to => ({
        name: PATHS.SOR.LISTING_OBJECT.name,
        params: {
          objectType: to.params.objectType || 'assessments',
        },
      }),
    },
    {
      ...PATHS.SOR.LISTING_OBJECT,
      component: SorListingPage,
      props: true,
      beforeEnter: multiguard([canNavigateToNewSor(store), canNavigateToRoute(store, allRoles)]),
    },
    {
      ...PATHS.PROGRAM_SCOPING.PROGRAM,
      component: ProgramScopingPage,
      beforeEnter: multiguard([
        behindFeatureFlag(store, 'program_onboarding_beta'),
        canNavigateToRoute(store, adminRoles),
      ]),
    },
    {
      ...PATHS.PROGRAM_SCOPING.PROGRAM_OBJECT,
      component: PSSOScopingPage,
      props: true,
      beforeEnter: multiguard([
        behindFeatureFlag(store, 'program_onboarding_beta'),
        canNavigateToRoute(store, adminRoles),
      ]),
    },
    {
      ...PATHS.PROFILE,
      component: ProfilePage,
      beforeEnter: multiguard([canNavigateToRoute(store, allRoles)]),
    },
    {
      ...PATHS.SETTINGS.PEOPLE_ROLES,
      component: Embedded,
      beforeEnter: canNavigateToRoute(store, adminRoles),
      props: { pageTitle: 'People' },
    },
    {
      ...PATHS.SETTINGS.EMAIL,
      component: EmailSettingsPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SETTINGS.ZENCONNECT,
      component: ConnectorsPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SETTINGS.SERVICENOW.SETTINGS,
      component: servicenowSettings,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SETTINGS.SERVICENOW.STEP1,
      component: servicenowStep1Settings,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SETTINGS.SAML,
      component: SAMLSettingsPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
      children: [
        {
          ...PATHS.SETTINGS.SAML_SAVE,
          component: SAMLSettingsPage,
          beforeEnter: canNavigateToRoute(store, adminRoles),
        },
      ],
    },
    {
      ...PATHS.SETTINGS.INTEGRATIONS,
      component: IntegrationSettingsPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SETTINGS.RISK,
      component: RiskSettingsPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.WORKFLOWS,
      component: WorkflowsPage,
      beforeEnter: multiguard([behindFeatureFlag(store, 'extended_workflows'), canNavigateToRoute(store, adminRoles)]),
    },
    {
      ...PATHS.SETTINGS.AUTHENTICATION,
      component: AuthenticationSettingsPage,
      beforeEnter: multiguard([behindFeatureFlag(store, 'new_sso'), canNavigateToRoute(store, adminRoles)]),
    },
    {
      ...PATHS.SETTINGS.AUTHENTICATION_SAML,
      props: true,
      component: AuthenticationSettingsSaml,
      beforeEnter: multiguard([behindFeatureFlag(store, 'new_sso'), canNavigateToRoute(store, adminRoles)]),
    },
    {
      ...PATHS.SETTINGS.SYSTEM,
      component: Embedded,
      props: { pageTitle: 'System settings' },
    },
    {
      ...PATHS.SETTINGS.STORAGE.INTEGRATION,
      component: Embedded,
      props: { pageTitle: 'Storage integrations' },
    },
    {
      ...PATHS.SETTINGS.CUSTOM_ATTRIBUTES,
      component: Embedded,
      props: { pageTitle: 'Custom Attribute Management' },
    },
    {
      ...PATHS.SURVEY.BUILDER,
      component: SurveyBuilderPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.SURVEY.SENDING,
      component: SurveySendingPage,
      beforeEnter: canNavigateToRoute(store, ['Administrator', 'Editor', 'Contributor']),
    },
    {
      ...PATHS.SURVEY.RESENDING,
      component: SurveyReSendingPage,
    },
    {
      ...PATHS.AUDIT.LIST,
      component: Embedded,
      props: { pageTitle: 'Audits' },
    },
    {
      ...PATHS.AUDIT.WIZARD,
      redirect: '/reports/audits/wizard/new',
      component: AuditSetupWizard,
      beforeEnter: canNavigateToRoute(store, adminRoles),
      children: [
        {
          ...PATHS.AUDIT.NEW,
          component: Embedded,
          beforeEnter: canNavigateToRoute(store, adminRoles),
        },
        {
          ...PATHS.AUDIT.EDIT,
          component: Embedded,
          beforeEnter: canNavigateToRoute(store, adminRoles),
        },
      ],
    },
    {
      ...PATHS.IMPORT,
      component: ImportPage,
      beforeEnter: canNavigateToRoute(store, allRoles),
    },
    {
      ...PATHS.EXPORT,
      component: ExportPage,
      beforeEnter: canNavigateToRoute(store, adminRoles),
    },
    {
      ...PATHS.RISK_HEATMAP,
      component: RiskHeatmapPage,
      beforeEnter: canNavigateToRoute(store),
    },
    {
      ...PATHS.WATCH_UNSUBSCRIBE,
      component: WatchUnsubscribePage,
      beforeEnter: canNavigateToRoute(store, allRoles),
    },
    {
      ...PATHS.NEW_OBJECT,
      props: true,
      component: NewObjectPage,
      beforeEnter: multiguard([
        behindFeatureFlag(store, 'dynamic_form_creation'),
        canNavigateToRoute(store, adminRoles),
      ]),
    },
    {
      ...PATHS.DASHBOARD.EMBEDDED,
      props: true,
      component: EmbeddedDashboardPage,
    },
    {
      ...PATHS.DASHBOARD.LIST,
      props: true,
      component: DashboardListPage,
    },
    {
      ...PATHS.DASHBOARD.NEW,
      component: NewDashboardPage,
      beforeEnter: multiguard([canNavigateToRoute(store, adminRoles)]),
    },
    {
      ...PATHS.DASHBOARD.ITEM,
      props: true,
      component: DashboardPage,
    },
    {
      ...PATHS.COMPLIANCE.DASHBOARD,
      component: Embedded,
      props: { pageTitle: 'Compliance dashboard' },
    },
    {
      ...PATHS.OBJECT_MAPPER,
      component: ObjectMapperPage,
      props: (route: Route) => ({ ...keysToCamel(route.query), ...route.params }),
      beforeEnter: canNavigateToRoute(store, allRoles),
    },
    {
      ...PATHS.BENCHMARKS,
      component: Benchmarks,
    },
    {
      ...PATHS.BENCHMARK,
      component: Benchmark,
      props: true,
    },
    {
      ...PATHS.RISK_ALLOCATION,
      component: RiskAllocation,
      props: true,
    },
    {
      ...PATHS.DEVELOPERS,
      component: Embedded,
      props: { pageTitle: 'Developers' },
    },
    {
      ...PATHS.NOT_FOUND,
      component: Error404Page,
    },
  ];
}
